import { Star, Users, Clock, ShieldCheck } from 'lucide-react'

interface FunnelSocialProofRailProps {
  avgRating: number | null
  totalReviews: number
  recentLeadCount: number
}

/**
 * Server-rendered horizontal trust rail for the multi-step devis funnel.
 *
 * Always shows two evergreen baseline signals (SIRENE-vérifiés count + 48h
 * promise) so the rail never disappears on low-volume periods. Adds the
 * dynamic rating + recent-lead segments when their thresholds are met.
 * Loader is the single source of truth для dynamic values — no client-only
 * Date/random/window reads (avoids hydration #418).
 */
export function FunnelSocialProofRail({
  avgRating,
  totalReviews,
  recentLeadCount,
}: FunnelSocialProofRailProps) {
  const ratingValid = typeof avgRating === 'number' && avgRating > 0 && totalReviews > 0
  const leadsValid = recentLeadCount > 0

  return (
    <div
      aria-label="Indicateurs de confiance AfficheServices"
      className="flex flex-wrap items-center justify-center gap-x-4 gap-y-2 rounded-xl border border-emerald-200 bg-emerald-50 px-3 py-2.5 text-sm font-medium text-emerald-900"
    >
      {/* Evergreen baseline #1 — always renders, doesn't need DB data. */}
      <span className="flex items-center gap-1.5">
        <ShieldCheck className="h-4 w-4 text-emerald-600" aria-hidden="true" />
        <strong className="font-semibold">110 000+</strong>
        <span className="text-emerald-700">artisans SIRET vérifiés</span>
      </span>

      <span className="hidden h-3 w-px bg-emerald-300/50 sm:block" aria-hidden="true" />

      {ratingValid && (
        <>
          <span className="flex items-center gap-1.5">
            <Star className="h-4 w-4 fill-amber-400 text-amber-400" aria-hidden="true" />
            <strong className="font-semibold">{avgRating!.toFixed(1)}/5</strong>
            <span className="text-emerald-700">· {totalReviews.toLocaleString('fr-FR')} avis</span>
          </span>
          <span className="hidden h-3 w-px bg-emerald-300/50 sm:block" aria-hidden="true" />
        </>
      )}

      {leadsValid && (
        <>
          <span className="flex items-center gap-1.5">
            <Users className="h-4 w-4 text-emerald-600" aria-hidden="true" />
            <strong className="font-semibold">{recentLeadCount.toLocaleString('fr-FR')}</strong>
            <span className="text-emerald-700">devis traités ces 30 derniers jours</span>
          </span>
          <span className="hidden h-3 w-px bg-emerald-300/50 sm:block" aria-hidden="true" />
        </>
      )}

      {/* Evergreen baseline #2 — promise visible even without volume signal. */}
      <span className="flex items-center gap-1.5">
        <Clock className="h-4 w-4 text-emerald-600" aria-hidden="true" />
        Réponses sous 48h
      </span>
    </div>
  )
}
